import { useState, useEffect } from 'react'
import { useAuth } from './useAuth'

interface UserRoleInfo {
  id: string
  email: string
  role: string
}

export function useUserRole() {
  const { session, user } = useAuth()
  const [userRole, setUserRole] = useState<UserRoleInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchUserRole = async () => {
      // Don't fetch if no session or user
      if (!session?.access_token || !user) {
        setUserRole(null)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
        const response = await fetch(`${apiUrl}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        })

        if (!response.ok) {
          // If unauthorized, fall back to user metadata
          if (response.status === 401) {
            const role = user.user_metadata?.role || user.app_metadata?.role || 'user'
            setUserRole({
              id: user.id,
              email: user.email || '',
              role: role
            })
            return
          }
          throw new Error('Failed to fetch user role')
        }

        const data = await response.json()
        if (data.success) {
          setUserRole(data.user)
        } else {
          throw new Error('Invalid response format')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
        console.error('Error fetching user role:', err)
        // Fallback to user metadata if API fails
        const role = user.user_metadata?.role || user.app_metadata?.role || 'user'
        setUserRole({
          id: user.id,
          email: user.email || '',
          role: role
        })
      } finally {
        setLoading(false)
      }
    }

    fetchUserRole()
  }, [session?.access_token, user?.id]) // Only depend on session token and user ID

  return {
    userRole,
    loading,
    error,
    isAdmin: userRole?.role === 'admin',
  }
}
